import React from 'react';
import { Link } from 'react-router-dom';
import { Task, TaskStatus, TaskPriority, TaskType } from '../../types/task.types';
import {
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ChatBubbleLeftIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import taskService from '../../services/taskService';

interface TaskTableProps {
  tasks: Task[];
  loading: boolean;
  selectedTasks: number[];
  onTaskClick: (task: Task) => void;
  onSelectionChange: (taskIds: number[]) => void;
  onTaskUpdated: () => void;
}

const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  loading,
  selectedTasks,
  onTaskClick,
  onSelectionChange,
  onTaskUpdated,
}) => {
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onSelectionChange(tasks.map((t) => t.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectTask = (taskId: number) => {
    if (selectedTasks.includes(taskId)) {
      onSelectionChange(selectedTasks.filter((id) => id !== taskId));
    } else {
      onSelectionChange([...selectedTasks, taskId]);
    }
  };

  const handleQuickComplete = async (e: React.MouseEvent, taskId: number) => {
    e.stopPropagation();
    try {
      await taskService.completeTask(taskId);
      onTaskUpdated();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-neutral-500">Loading tasks...</p>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <ClockIcon className="mx-auto h-12 w-12 text-neutral-400" />
        <h3 className="mt-2 text-sm font-medium text-neutral-900">No tasks found</h3>
        <p className="mt-1 text-sm text-neutral-500">Get started by creating a new task.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th scope="col" className="w-12 px-6 py-3">
              <input
                type="checkbox"
                checked={selectedTasks.length === tasks.length}
                onChange={handleSelectAll}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Task
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Priority
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Due Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Assigned
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Related To
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-200">
          {tasks.map((task) => (
            <tr
              key={task.id}
              onClick={() => onTaskClick(task)}
              className="hover:bg-neutral-50 cursor-pointer transition-colors"
            >
              <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedTasks.includes(task.id)}
                  onChange={() => handleSelectTask(task.id)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
              </td>
              <td className="px-6 py-4">
                <div className="flex items-start">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-neutral-900">{task.title}</div>
                    {task.description && (
                      <div className="text-sm text-neutral-500 truncate max-w-md">
                        {task.description}
                      </div>
                    )}
                    <div className="flex items-center mt-1 space-x-3 text-xs text-neutral-500">
                      {task._count && task._count.comments > 0 && (
                        <span className="flex items-center">
                          <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                          {task._count.comments}
                        </span>
                      )}
                      {task.tags.length > 0 && (
                        <div className="flex gap-1">
                          {task.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-800"
                            >
                              {tag}
                            </span>
                          ))}
                          {task.tags.length > 2 && (
                            <span className="text-neutral-400">+{task.tags.length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <TaskTypeBadge type={task.type} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <PriorityBadge priority={task.priority} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <StatusBadge status={task.status} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                {task.dueDateTime ? (
                  <div className={isOverdue(task) ? 'text-error-600 font-medium' : ''}>
                    {format(new Date(task.dueDateTime), 'MMM d, yyyy')}
                    <div className="text-xs text-neutral-400">
                      {format(new Date(task.dueDateTime), 'h:mm a')}
                    </div>
                  </div>
                ) : (
                  <span className="text-neutral-400">No due date</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {task.assignments.length > 0 ? (
                  <div className="flex items-center">
                    <UserGroupIcon className="h-4 w-4 text-neutral-400 mr-1" />
                    <span className="text-sm text-neutral-900">
                      {task.assignments[0].user.firstName || task.assignments[0].user.email}
                    </span>
                    {task.assignments.length > 1 && (
                      <span className="ml-1 text-xs text-neutral-500">
                        +{task.assignments.length - 1}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-neutral-400">Unassigned</span>
                )}
              </td>
              <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-1">
                  {/* Display Clients */}
                  {task.taskClients && task.taskClients.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {task.taskClients.map((tc) => (
                        <Link
                          key={tc.id}
                          to={`/clients/${tc.client.id}`}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary-100 text-primary-800 hover:bg-primary-200"
                        >
                          <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                          {tc.client.name}
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Display Deals */}
                  {task.taskDeals && task.taskDeals.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {task.taskDeals.map((td) => (
                        <Link
                          key={td.id}
                          to={`/deals/${td.deal.id}`}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-success-100 text-success-800 hover:bg-success-200"
                        >
                          <CurrencyDollarIcon className="h-3 w-3 mr-1" />
                          {td.deal.description}
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* No relations */}
                  {(!task.taskClients || task.taskClients.length === 0) &&
                   (!task.taskDeals || task.taskDeals.length === 0) && (
                    <span className="text-sm text-neutral-400">-</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                {task.status !== TaskStatus.COMPLETED && (
                  <button
                    onClick={(e) => handleQuickComplete(e, task.id)}
                    className="text-primary-600 hover:text-primary-900 font-medium"
                  >
                    <CheckCircleIcon className="h-5 w-5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Helper Components
const TaskTypeBadge: React.FC<{ type: TaskType }> = ({ type }) => {
  const colors = {
    CALL: 'bg-primary-100 text-primary-800',
    EMAIL: 'bg-secondary-100 text-secondary-800',
    MEETING: 'bg-success-100 text-success-800',
    TODO: 'bg-neutral-100 text-neutral-800',
    FOLLOW_UP: 'bg-warning-100 text-warning-800',
    CUSTOM: 'bg-secondary-100 text-secondary-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[type]}`}>
      {type.replace('_', ' ')}
    </span>
  );
};

const PriorityBadge: React.FC<{ priority: TaskPriority }> = ({ priority }) => {
  const colors = {
    LOW: 'bg-neutral-100 text-neutral-600',
    NORMAL: 'bg-primary-100 text-primary-600',
    MEDIUM: 'bg-warning-100 text-warning-600',
    HIGH: 'bg-warning-200 text-warning-700',
    URGENT: 'bg-error-100 text-error-600',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[priority]}`}>
      {priority}
    </span>
  );
};

const StatusBadge: React.FC<{ status: TaskStatus }> = ({ status }) => {
  const colors = {
    NOT_STARTED: 'bg-neutral-100 text-neutral-800',
    TODO: 'bg-primary-100 text-primary-800',
    IN_PROGRESS: 'bg-warning-100 text-warning-800',
    COMPLETED: 'bg-success-100 text-success-800',
    CANCELLED: 'bg-error-100 text-error-800',
    DEFERRED: 'bg-secondary-100 text-secondary-800',
    OVERDUE: 'bg-error-100 text-error-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}>
      {status.replace('_', ' ')}
    </span>
  );
};

const isOverdue = (task: Task): boolean => {
  if (!task.dueDateTime || task.status === TaskStatus.COMPLETED) return false;
  return new Date(task.dueDateTime) < new Date();
};

export default TaskTable;

